import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/StarRating";
import { Loader2, X } from "lucide-react";

interface ProductReviewsDialogProps {
  productId: string;
  productTitle: string;
  open: boolean;
  onClose: () => void;
}

interface ReviewRow {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  buyer_id: string;
}

/**
 * Popup plein écran en lecture seule listant les avis d'un produit.
 * L'écriture d'un avis reste réservée à « Mes achats et commandes ».
 */
export const ProductReviewsDialog = ({ productId, productTitle, open, onClose }: ProductReviewsDialogProps) => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [buyers, setBuyers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("product_reviews")
        .select("id, rating, comment, created_at, buyer_id")
        .eq("product_id", productId)
        .order("created_at", { ascending: false });

      const rows = (data ?? []) as ReviewRow[];
      // Seuls les champs publics du profil acheteur sont lus (jamais l'email).
      const ids = Array.from(new Set(rows.map((r) => r.buyer_id)));
      let names: Record<string, string> = {};
      if (ids.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, full_name, username")
          .in("id", ids);
        names = Object.fromEntries(
          (profiles ?? []).map((p) => [p.id, p.username || p.full_name || "Utilisateur"]),
        );
      }

      if (cancelled) return;
      setReviews(rows);
      setBuyers(names);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex max-w-2xl flex-col gap-0 overflow-hidden p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="flex-row items-start justify-between gap-3 border-b p-4 space-y-0">
          <div className="min-w-0">
            <DialogTitle className="text-lg">Avis</DialogTitle>
            <p className="text-sm text-muted-foreground truncate">{productTitle}</p>
          </div>
          <Button variant="ghost" size="icon" aria-label="Fermer" onClick={onClose}>
            <X className="h-5 w-5" aria-hidden="true" />
          </Button>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucun avis pour le moment.</p>
          ) : (
            reviews.map((review) => (
              <article key={review.id} className="rounded-lg border p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <StarRating rating={review.rating} size="sm" />
                  <span className="text-xs text-muted-foreground">{formatDate(review.created_at)}</span>
                </div>
                <p className="text-sm font-medium">{buyers[review.buyer_id] || "Utilisateur"}</p>
                {review.comment && (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.comment}</p>
                )}
              </article>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductReviewsDialog;
